import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "dhruva_anon_id";
const UTM_STORAGE_KEY = "dhruva_utm";
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];

function getAnonId() {
  let id = localStorage.getItem(STORAGE_KEY);
  if (!id) {
    id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(STORAGE_KEY, id);
  }
  return id;
}

/**
 * UTM capture — first-touch attribution.
 *
 * When a visitor arrives via a URL like /?utm_source=instagram&utm_campaign=july-launch,
 * we parse the UTMs and stash them in localStorage under `dhruva_utm`. Every
 * subsequent page_view carries the same UTMs until:
 *   1. The visitor arrives with a NEW `utm_source` in the URL (a fresh campaign
 *      click) — the stored UTMs are overwritten. This matches Google Analytics'
 *      session-based attribution.
 *   2. The visitor signs up — the backend copies UTMs to `users.first_utm_*`
 *      on the first authenticated request, then the localStorage entry can
 *      safely stay put (subsequent events keep sending it but the user doc
 *      is already stamped).
 *
 * Returns the currently-effective UTM bag. Empty object if none were ever seen.
 */
function captureAndReturnUtms(search) {
  const params = new URLSearchParams(search || "");
  const fromUrl = {};
  UTM_KEYS.forEach((k) => {
    const v = params.get(k);
    if (v && v.trim()) fromUrl[k] = v.trim().slice(0, 200); // clip absurdly long UTMs
  });
  // Fresh campaign click? Overwrite stored UTMs so we don't cross-attribute.
  if (fromUrl.utm_source) {
    try { localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(fromUrl)); } catch (_) {}
    return fromUrl;
  }
  // No new UTMs in this URL — reuse whatever we captured previously (first-touch).
  try {
    const stored = localStorage.getItem(UTM_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (_) { return {}; }
}

/**
 * Tiny visitor analytics — fires page_view to /api/public/track on every route change.
 * Anonymous ID is persisted in localStorage; no PII transmitted. UTM parameters
 * from the URL are captured on arrival and forwarded to the backend so paid
 * campaigns (Instagram Reels, WhatsApp broadcasts, ...) can be attributed
 * with first-touch accuracy in the Admin Visitors dashboard.
 */
export default function VisitorTracker() {
  const loc = useLocation();
  const last = useRef("");

  useEffect(() => {
    const path = loc.pathname + loc.search;
    if (path === last.current) return;
    last.current = path;
    const utms = captureAndReturnUtms(loc.search);
    axios.post(`${API}/public/track`, {
      event: "page_view",
      anon_id: getAnonId(),
      path,
      referrer: document.referrer || null,
      ...utms,
    }).catch(() => {});
  }, [loc]);

  return null;
}

export function trackEvent(event, meta = {}) {
  let utms = {};
  try {
    const stored = localStorage.getItem(UTM_STORAGE_KEY);
    if (stored) utms = JSON.parse(stored);
  } catch (_) {}
  axios.post(`${API}/public/track`, {
    event,
    anon_id: getAnonId(),
    path: window.location.pathname,
    meta,
    ...utms,
  }).catch(() => {});
}
